import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { useSubsectionContext } from '../SubsectionContext';
import { useCardBroadcast } from './cardBroadcast';
import { CardCollapsedSummary, CardViewStateControl, type CardSummaryRow } from './cardViewState';
import { useCardViewController } from './useCardViewController';

/** One field/value pair for a card's collapsed read-only summary (the eye view). */
export type CollapsibleCardSummaryRow = CardSummaryRow;

interface CollapsibleCardProps {
  /** Used both as the visible heading and as a slug for the heading id. */
  readonly title: string;
  /** Stable id prefix so multiple cards with the same title don't collide. */
  readonly idPrefix: string;
  /**
   * DOM id rendered on the outer Card. When this id is one of the
   * subsections of the active section (see `SubsectionContext`), the card
   * goes "managed" — its open state is owned by the section's tab strip
   * (exactly one card open at a time). Without a matching context, the
   * card keeps its local open/closed state.
   */
  readonly anchorId?: string;
  readonly count?: number;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
  /** Optional element rendered to the right of the count badge (e.g. a unit toggle). */
  readonly headerAction?: ReactNode;
  /**
   * When true, the body renders flush against the card edges (no
   * padding). Use for content that owns its own grid + dividers, e.g.
   * V1 deflist rows.
   */
  readonly flush?: boolean;
  /**
   * Field/value pairs for the collapsed read-only summary. When provided, an eye toggle appears in
   * the header cycling off → filled → all; in the filled/all modes the COLLAPSED card shows a compact
   * label:value list instead of just the title. (PTV2-1157, first iteration.)
   */
  readonly summary?: readonly CollapsibleCardSummaryRow[];
  /**
   * PTV2-1157 — when this number changes to a new non-zero value, the card force-expands (used by the
   * node-editor required-field flash to reveal a collapsed card before ringing its rows). Ignored in
   * locked/view-only mode, which can't expand.
   */
  readonly forceOpenSignal?: number;
}

/**
 * Stack-friendly collapsible card used by the patient-record key-value
 * sections (Demographics, Medical History). The header is a button so
 * the whole row toggles; an optional `headerAction` slot sits to the
 * right of the count badge for in-card controls (e.g. metric/imperial
 * unit toggle in measurements).
 */
export function CollapsibleCard({
  title,
  idPrefix,
  anchorId,
  count,
  defaultOpen = true,
  children,
  headerAction,
  flush = false,
  summary,
  forceOpenSignal,
}: CollapsibleCardProps): ReactElement {
  const ctx = useSubsectionContext();
  const managed = ctx !== null && anchorId !== undefined && ctx.managedIds.has(anchorId);

  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = managed ? ctx.openIds.has(anchorId) : uncontrolledOpen;

  const setOpenState = (next: boolean): void => {
    if (managed) {
      ctx.setOpen(anchorId, next);
    } else {
      setUncontrolledOpen(next);
    }
  };

  const toggle = (): void => {
    setOpenState(!open);
  };

  // Collapsed-summary sub-mode + 4-state view control + "set all" broadcast adoption.
  const { summaryView, viewState, applyViewState } = useCardViewController(open, setOpenState);

  // Read-only / view-only mode (pedigree Lock): the broadcast is `forced`. The card can't be
  // expanded — the header isn't a toggle and shows no chevron (PTV2-1157). No-op elsewhere (the
  // patient record never sets `forced`).
  const locked = useCardBroadcast()?.forced === true;

  // PTV2-1157 — force-expand when `forceOpenSignal` bumps to a new non-zero value (required-field
  // flash). A ref tracks the last handled signal so it fires only on change, never on mount. No-op
  // while locked (view-only can't expand).
  const lastForceOpenRef = useRef<number | undefined>(forceOpenSignal);
  useEffect(() => {
    if (forceOpenSignal === undefined || forceOpenSignal === 0) return;
    if (forceOpenSignal === lastForceOpenRef.current) return;
    lastForceOpenRef.current = forceOpenSignal;
    if (!locked && !open) {
      setOpenState(true);
    }
    // setOpenState is stable enough for this one-shot; deps kept minimal on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpenSignal, locked]);

  const headingId = `${idPrefix}-${title.toLowerCase().replace(/\s+/g, '-')}-heading`;

  const showSummary = !open && summary !== undefined && summaryView !== 'off';

  return (
    <Card
      aria-labelledby={headingId}
      {...(anchorId !== undefined ? { id: anchorId } : {})}
      className="scroll-mt-14"
    >
      <CardHeader
        className={cn(
          'px-5 py-3.5 space-y-0',
          // Show the header divider when the section is open OR a collapsed summary follows.
          (open || showSummary) && 'border-b border-border',
        )}
      >
        {/* flex-wrap so the eye/action controls drop to a second row (and the header grows) instead of
            being clipped when the panel is very narrow; the toggle button keeps a preferred basis so
            they only wrap when there genuinely isn't room. */}
        <div data-card-header-row className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {(() => {
            const headerInner = (
              <>
                {!locked && (
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      'h-4 w-4 text-muted-foreground transition-transform shrink-0',
                      open && 'rotate-180',
                    )}
                  />
                )}
                <CardTitle
                  id={headingId}
                  className={cn(
                    'text-[13.5px] font-semibold tracking-tight min-w-0 flex-1 truncate transition-colors',
                    !locked && 'group-hover:text-primary',
                  )}
                >
                  {title}
                </CardTitle>
                {count !== undefined && (
                  <span
                    className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-xs font-medium tabular-nums shrink-0"
                    aria-label={`${String(count)} items`}
                  >
                    {count}
                  </span>
                )}
              </>
            );
            // Locked (view-only): a non-interactive header — not clickable, no chevron.
            return locked ? (
              <div className="flex min-w-0 flex-[1_1_10rem] items-center gap-2 text-left">
                {headerInner}
              </div>
            ) : (
              <button
                type="button"
                onClick={toggle}
                aria-expanded={open}
                className="flex min-w-0 flex-[1_1_10rem] items-center gap-2 text-left group"
              >
                {headerInner}
              </button>
            );
          })()}
          {(summary !== undefined || (open && headerAction !== undefined)) && (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {summary !== undefined && (
                <CardViewStateControl variant="hover" value={viewState} onChange={applyViewState} className="shrink-0" />
              )}
              {open && headerAction !== undefined && <div className="shrink-0">{headerAction}</div>}
            </div>
          )}
        </div>
      </CardHeader>
      {open && <CardContent className={flush ? 'p-0' : 'p-5'}>{children}</CardContent>}
      {showSummary && (
        <CardContent className="px-5 py-2.5">
          <CardCollapsedSummary rows={summary} summaryView={summaryView} />
        </CardContent>
      )}
    </Card>
  );
}
